import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';

/** Content available to assistive technology but visually hidden. */
export function VisuallyHidden({ children }: { children: ReactNode }) {
  return <span className="sr-only">{children}</span>;
}

/**
 * Skip link. Rendered as the first focusable element in every layout so keyboard
 * and screen-reader users can bypass navigation and, on public routes, the
 * decorative canvas.
 */
export function SkipLink({
  targetId = 'main-content',
  href,
  children = 'Skip to main content',
}: {
  targetId?: string;
  /** Overrides the derived `#targetId` fragment. */
  href?: string;
  children?: ReactNode;
}) {
  return (
    <a
      href={href ?? `#${targetId}`}
      className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:rounded-md focus:bg-warm-white focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-near-black focus:shadow-lg focus:outline-hidden focus:ring-2 focus:ring-warm-white focus:ring-offset-2 focus:ring-offset-near-black"
    >
      {children}
    </a>
  );
}

export function Spinner({
  size = 'md',
  label = 'Loading',
  className,
}: {
  size?: 'sm' | 'md' | 'lg';
  label?: string;
  className?: string;
}) {
  const dimension = size === 'sm' ? 'size-4' : size === 'lg' ? 'size-8' : 'size-5';
  return (
    <span role="status" className={cn('inline-flex items-center gap-2', className)}>
      <svg
        className={cn(dimension, 'animate-spin text-current')}
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
        <path
          d="M22 12a10 10 0 0 0-10-10"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </svg>
      <VisuallyHidden>{label}</VisuallyHidden>
    </span>
  );
}

/**
 * Loading placeholder. `aria-hidden` because the surrounding region announces
 * its own busy state - a screen reader should hear "loading sessions", not a
 * description of grey rectangles.
 */
export function Skeleton({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn('block animate-pulse rounded-md bg-graphite/60', className)}
    />
  );
}

export function Divider({
  className,
  orientation = 'horizontal',
}: {
  className?: string;
  orientation?: 'horizontal' | 'vertical';
}) {
  return (
    <span
      role="separator"
      aria-orientation={orientation}
      className={cn(
        'block bg-white/10',
        orientation === 'horizontal' ? 'h-px w-full' : 'h-full w-px',
        className,
      )}
    />
  );
}

/**
 * The warm-white hairline used as a section connector across all three
 * experiences. Purely decorative.
 */
export function BrandRule({ className, accent }: { className?: string; accent?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'block h-px w-full bg-gradient-to-r from-transparent via-current to-transparent opacity-40',
        accent ?? 'text-warm-white',
        className,
      )}
    />
  );
}

/** Screen-reader live region for status announcements. */
export function LiveRegion({
  message,
  assertive = false,
}: {
  message: string | null;
  assertive?: boolean;
}) {
  return (
    <div
      role="status"
      aria-live={assertive ? 'assertive' : 'polite'}
      aria-atomic="true"
      className="sr-only"
    >
      {message ?? ''}
    </div>
  );
}
